import { useState, useEffect } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Heart, Hand, Sparkles, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const SinglesAtBar = ({ barId, barName }) => {
  const [singles, setSingles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [wavingAt, setWavingAt] = useState(null);
  const [hasMatchmaking, setHasMatchmaking] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSingles();
    }
  }, [isOpen, barId]);

  const fetchSingles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/matchmaking/singles-at-bar/${barId}`);
      setSingles(response.data.singles || []);
      setHasMatchmaking(true);
    } catch (e) {
      if (e.response?.status === 403) {
        setHasMatchmaking(false);
      } else {
        console.error(e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWave = async (userId) => {
    setWavingAt(userId);
    try {
      const response = await axios.post(`${API}/matchmaking/wave/${userId}`);
      if (response.data.is_match) {
        toast.success('💕 It\'s a match! Go say hi!', { duration: 5000 });
      } else {
        toast.success('👋 Wave sent!');
      }
      fetchSingles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send wave');
    } finally {
      setWavingAt(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-pink-500/50 text-pink-400 hover:bg-pink-500/10"
        >
          <Heart className="w-4 h-4 mr-2" />
          Single & Ready to Mingle
        </Button>
      </DialogTrigger>
      <DialogContent className="glass border border-slate-700 max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pink-400" />
            Singles Ready to Mingle at {barName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 text-pink-400 animate-spin mx-auto" />
          </div>
        ) : !hasMatchmaking ? (
          <div className="text-center py-8">
            <Heart className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Enable Matchmaking</h3>
            <p className="text-slate-400 text-sm mb-4">
              Turn on "Single & Mingling" mode to see other singles at this bar.
            </p>
            <Button
              onClick={() => window.location.href = '/matchmaking'}
              className="bg-pink-600 hover:bg-pink-700"
            >
              Go to Matchmaking
            </Button>
          </div>
        ) : singles.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Singles Yet</h3>
            <p className="text-slate-400 text-sm">
              Be the first to activate matchmaking at this bar!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {singles.map((single) => (
              <Card
                key={single.user_id}
                className="glass p-4 border border-slate-700 hover:border-pink-500/50 transition-all"
              >
                <div className="flex items-start gap-3">
                  <Avatar className="w-12 h-12 border-2 border-pink-500">
                    {single.profile_picture_url ? (
                      <AvatarImage src={`${process.env.REACT_APP_BACKEND_URL}${single.profile_picture_url}`} />
                    ) : null}
                    <AvatarFallback className="bg-pink-600 text-white">
                      {single.username?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-white">{single.username}</p>
                        <p className="text-sm text-slate-400">{single.age} years old</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleWave(single.user_id)}
                        disabled={wavingAt === single.user_id}
                        className="bg-pink-600 hover:bg-pink-700"
                      >
                        {wavingAt === single.user_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Hand className="w-4 h-4 mr-1" />
                            Wave
                          </>
                        )}
                      </Button>
                    </div>
                    {single.bio && (
                      <p className="text-sm text-slate-300 mt-2">{single.bio}</p>
                    )}
                    {single.interests && single.interests.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {single.interests.slice(0, 4).map((interest, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-pink-500/20 text-pink-300 text-xs rounded-full"
                          >
                            {interest}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SinglesAtBar;
